import maplibregl from 'maplibre-gl'
import Supercluster from 'supercluster'

export interface LocationClusterPoint {
  key: string
  lat: number
  lng: number
  count: number
  icon: string
}

interface PointProps {
  key: string
  count: number
  icon: string
}

interface ClusterProps {
  sum: number
}

export interface LocationClusterHandlers {
  onClick(key: string): void
  /** entering=false fires on that same marker's mouseleave (not "hover cleared" globally) --
   * lets the consumer ignore a leave that's stale because another marker's enter already fired. */
  onHoverChange(key: string, entering: boolean): void
}

const SC_RADIUS = 60
// Below this the underlying data refresh already renders one marker per location; clustering
// past it would just hide real, already-close-enough-to-click pins for no benefit.
const SC_MAX_ZOOM = 14

/**
 * Clusters the AssetsMap location markers (one rich HTML marker per unique lat/lng) so
 * zooming out to country level doesn't dump dozens of overlapping pins. MapLibre's native
 * clustering only works on GeoJSON sources, so for these HTML markers (icon + count badge)
 * supercluster is run directly here -- same approach as vehicle-cluster.ts, but simpler:
 * this manager owns marker creation/removal itself instead of delegating to a host, since
 * AssetsMap already rebuilds its whole marker set on every data refresh anyway.
 */
export class LocationClusterManager {
  private readonly map: maplibregl.Map
  private readonly handlers: LocationClusterHandlers
  private readonly index: Supercluster<PointProps, ClusterProps>
  private markers: maplibregl.Marker[] = []
  private markersByKey: Record<string, maplibregl.Marker> = {}
  // supercluster has no data (and getClusters() throws) until the first load() -- init()
  // wires up the moveend listener synchronously, but the first update() only arrives once
  // the async data fetch resolves, and a container resize can fire moveend in that gap.
  private hasData = false
  private readonly onMoveEnd = () => this.render()

  constructor(map: maplibregl.Map, handlers: LocationClusterHandlers) {
    this.map = map
    this.handlers = handlers
    this.index = new Supercluster<PointProps, ClusterProps>({
      radius: SC_RADIUS,
      maxZoom: SC_MAX_ZOOM,
      // Cluster bubbles show total device count (matches what a single-location marker
      // shows), not just how many locations got merged.
      map: (props) => ({ sum: props.count }),
      reduce: (accumulated, props) => {
        accumulated.sum += props.sum
      }
    })
  }

  /** Registers the viewport listener. Call once after the map is constructed. */
  init(): void {
    this.map.on('moveend', this.onMoveEnd)
  }

  /**
   * Refreshes the clustered point set (called on every data refresh, on its own timer --
   * not tied to map movement) and re-renders. Skips the render step while the map is mid
   * pan/zoom: re-clustering with a transient, still-animating zoom value would reposition
   * markers out from under the animation, making already-visible groups appear to jump.
   * moveend's own render() picks up this freshly loaded data once the gesture settles.
   */
  update(points: LocationClusterPoint[]): void {
    this.index.load(points.map((p) => ({
      type: 'Feature',
      properties: { key: p.key, count: p.count, icon: p.icon },
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] }
    })))
    this.hasData = true
    if (!this.map.isMoving()) {
      this.render()
    }
  }

  /** Toggles the highlight class on a location marker, if it's currently rendered unclustered. */
  setHighlighted(key: string | null, highlighted: boolean): void {
    const marker = key ? this.markersByKey[key] : null
    if (!marker) return
    marker.getElement().classList.toggle('datacake-marker-container--highlighted', highlighted)
  }

  destroy(): void {
    this.map.off('moveend', this.onMoveEnd)
    this.clear()
  }

  private render(): void {
    if (!this.map.style || !this.hasData) return
    this.clear()

    const bounds = this.map.getBounds()
    const clusters = this.index.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.round(this.map.getZoom())
    )

    // supercluster's own geometry (a weighted average of a cluster's members) is what to
    // render at: verified against production data that it's perfectly stable across zoom
    // levels for an unchanged member set, and -- unlike anchoring on a single "biggest"
    // member -- it moves the least when a cluster actually does split (the new child
    // centroids are each close to the parent's, since the parent is their average).
    clusters.forEach((feature) => {
      const props = feature.properties as any
      const lngLat = feature.geometry.coordinates as [number, number]
      if (props.cluster) {
        this.addClusterMarker(props.sum, props.cluster_id, lngLat)
      } else {
        this.addLocationMarker(props.key, props.count, props.icon, lngLat)
      }
    })
  }

  private addLocationMarker(key: string, count: number, icon: string, lngLat: [number, number]): void {
    const element = document.createElement('div')
    element.className = 'datacake-marker-container'
    element.innerHTML = `
      <div class="datacake-marker" data-marker-id="${key}">
        <div class="marker-badge">
          <i class="bi ${icon}"></i>
          <span class="marker-count">${count}</span>
        </div>
      </div>`

    element.addEventListener('click', (event) => {
      event.stopPropagation()
      this.handlers.onClick(key)
    })
    element.addEventListener('mouseenter', () => this.handlers.onHoverChange(key, true))
    element.addEventListener('mouseleave', () => this.handlers.onHoverChange(key, false))

    const marker = new maplibregl.Marker({ element, anchor: 'bottom' }).setLngLat(lngLat).addTo(this.map)
    this.markers.push(marker)
    this.markersByKey[key] = marker
  }

  private addClusterMarker(count: number, clusterId: number, lngLat: [number, number]): void {
    const element = document.createElement('div')
    element.className = 'loc-cluster'
    if (count >= 250) element.classList.add('loc-cluster--lg')
    else if (count >= 50) element.classList.add('loc-cluster--md')
    element.textContent = String(count)

    element.addEventListener('click', (event) => {
      event.stopPropagation()
      const expansionZoom = this.index.getClusterExpansionZoom(clusterId)
      this.map.easeTo({ center: lngLat, zoom: Math.min(expansionZoom, SC_MAX_ZOOM + 1) })
    })

    this.markers.push(new maplibregl.Marker({ element }).setLngLat(lngLat).addTo(this.map))
  }

  private clear(): void {
    this.markers.forEach((marker) => marker.remove())
    this.markers = []
    this.markersByKey = {}
  }
}
